<template>
  <div class="container v-content-container tooltip-wrapper popup-wrapper">
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <span v-if="selectedBookingId != null">
            {{ noDataExist ? "Venue Booking" : bookingName }}
          </span>
          <span v-else-if="!loading">
            {{ $label("booking.new_booking", "New Booking") }}
          </span>
        </h1>
      </div>
    </div>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <new-customer-modal
        :showModal="showAddCustomerModal"
        @close="closeAddCustomerModal"
        @onSuccess="onSelectCustomer"
      />

      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <!-- Tab panes -->
      <div v-show="!loading" class="tab-content tooltip-wrapper">
        <div id="form-booking-data">
          <div class="tab-pane active" id="booking-data">
            <div class="row">
              <div class="col-sm-6">
                <h2>
                  <i class="fa fa-fw fa-lg fa-dot-circle-o"></i>
                  {{ $label("booking.who", "Who") }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <div class="row">
                        <section class="col col-12">
                          <label class="label">{{
                            $label("booking.customer", "Customer")
                          }}</label>
                          <div class="flex-between">
                            <searchable-select
                              class="col-10"
                              v-model="selectedBooking.customer"
                              :searchResult="customers"
                              :placeholder="
                                $label(
                                  'booking.customer_search',
                                  'Search customer'
                                )
                              "
                              @change="onSearchChange"
                            >
                            </searchable-select>
                            <button
                              type="button"
                              class="btn btn-primary user-plus-button"
                              @click="openAddCustomerModal"
                            >
                              <i class="fa fa-user-plus"></i>
                            </button>
                          </div>
                          <p v-if="hasError('customer_id')" class="text-danger">
                            {{ getError("customer_id") }}
                          </p>
                        </section>
                      </div>

                      <!-- BEGIN Remarks -->
                      <section>
                        <label class="label">{{
                          $label("booking.remarks", "Remarks")
                        }}</label>
                        <label class="textarea">
                          <i class="icon-append fa fa-comment"></i>
                          <textarea
                            v-model="selectedBooking.remarks"
                            :disabled="updating"
                            id="booking-note"
                            rows="4"
                          >
                          </textarea>
                        </label>
                      </section>
                      <!-- END Remarks -->
                    </fieldset>
                  </div>
                </div>
              </div>

              <div class="col-sm-6 tooltip-wrapper">
                <h2>
                  <i class="fa fa-fw fa-lg fa-calendar"></i>
                  {{ $label("booking.when_price", "When | Where") }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <section>
                        <label class="label">
                          {{ $label("booking.from", "From") }}
                        </label>
                        <label class="flex-between">
                          <label class="input col-6 mr-1">
                            <i class="icon-append fa fa-calendar"></i>
                            <date-picker
                              v-model="selectedBooking.from.date"
                              :format="dateFormat"
                              :placeholder="dateFormat"
                              class="input"
                              id="booking-start-date"
                            >
                            </date-picker>
                          </label>
                          <label class="input col-6 ml-1">
                            <i class="icon-append fa fa-clock-o"></i>
                            <date-picker
                              v-model="selectedBooking.from.time"
                              :format="timeFormat"
                              :placeholder="timeFormat"
                              type="time"
                              class="input"
                              id="booking-start-time"
                            >
                            </date-picker>
                          </label>
                        </label>
                        <p
                          v-if="
                            hasError('starts_at') ||
                              hasError('venues.0.starts_at')
                          "
                          class="text-danger"
                        >
                          {{
                            getError("starts_at") ||
                              getError("venues.0.starts_at")
                          }}
                        </p>
                      </section>
                      <section>
                        <label class="label">
                          {{ $label("booking.to", "To") }}
                        </label>
                        <label class="flex-between">
                          <label class="input col-6 mr-1">
                            <i class="icon-append fa fa-calendar"></i>
                            <date-picker
                              v-model="selectedBooking.to.date"
                              :format="dateFormat"
                              :placeholder="dateFormat"
                              class="input"
                              id="booking-end-date"
                            >
                            </date-picker>
                          </label>

                          <label class="input col-6 ml-1">
                            <i class="icon-append fa fa-clock-o"></i>
                            <date-picker
                              v-model="selectedBooking.to.time"
                              :format="timeFormat"
                              :placeholder="timeFormat"
                              class="input"
                              type="time"
                              id="booking-end-time"
                            >
                            </date-picker>
                          </label>
                        </label>
                        <p
                          v-if="
                            hasError('ends_at') || hasError('venues.0.ends_at')
                          "
                          class="text-danger"
                        >
                          {{
                            getError("ends_at") || getError("venues.0.ends_at")
                          }}
                        </p>
                      </section>
                      <div class="flex-between">
                        <section class="col-12">
                          <label class="label">
                            {{ $label("booking.venues", "Venues") }}
                          </label>
                          <multiselect
                            v-model="selectedBooking.venues"
                            tag-placeholder=""
                            placeholder="Add booking type"
                            label="name"
                            track-by="id"
                            :options="venueOptions"
                            class="v-multiselect v-form"
                            :multiple="true"
                            :taggable="false"
                            :show-labels="false"
                            :close-on-select="true"
                          >
                          </multiselect>
                          <p
                            v-if="
                              hasError('venue_id') ||
                                hasError('venues.0.venue_id')
                            "
                            class="text-danger"
                            style="word-break: break-all"
                          >
                            {{
                              getError("venue_id") ||
                                getError("venues.0.venue_id")
                            }}
                          </p>
                        </section>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-8"><div class="col-xs-12">&nbsp;</div></div>
            <div class="row">
              <div class="col-sm-6">
                <h2>
                  <i class="fa fa-fw fa-lg fa-money"></i>
                  {{ $label("booking.price", "Price") }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <div class="flex-between">
                        <section class="col-9 mr-1">
                          <label class="label">
                            {{ $label("booking.price", "Price") }}
                          </label>
                          <label class="select">
                            <select
                              id="booking-price"
                              v-model="selectedBooking.price"
                              :disabled="selectedBooking.use_manual_price"
                            >
                              <option
                                v-for="price in prices"
                                :key="price.id"
                                :value="price.id"
                                >{{ price.name }}</option
                              >
                            </select>
                            <i></i>
                          </label>
                        </section>

                        <section class="col-3 ml-1">
                          <label class="label">
                            {{ $label("booking.discount", "Discount") }}
                          </label>
                          <label class="select">
                            <select
                              id="booking-discount"
                              v-model="selectedBooking.discount_id"
                              :disabled="selectedBooking.use_manual_price"
                            >
                              <option value="">-</option>
                              <option
                                v-for="discount in discounts"
                                :key="discount.id"
                                :value="discount.id"
                                >{{ discount.value }}</option
                              >
                            </select>
                            <i></i>
                          </label>
                        </section>
                      </div>

                      <div class="flex-between">
                        <section class="flex-1 mr-1">
                          <label class="label">
                            {{ $label("booking.oversteer", "Oversteer") }}
                          </label>
                          <label class="checkbox">
                            <input
                              type="checkbox"
                              id="booking-use-manual-price"
                              v-model="selectedBooking.use_manual_price"
                              :disabled="
                                prices.length === 0 &&
                                  selectedBooking.venues.length === 1
                              "
                            />
                            {{ $label("booking.manual_price", "Manual price") }}
                            <i></i>
                          </label>
                        </section>

                        <section class="flex-1 ml-1 mr-1">
                          <label class="label">
                            {{ $label("booking.vat", "Vat").toUpperCase() }}
                          </label>
                          <label class="input">
                            <label class="select">
                              <select
                                id="booking-manual-price-vat"
                                v-model="selectedBooking.manual_price_vat_id"
                                :disabled="
                                  updating || !selectedBooking.use_manual_price
                                "
                              >
                                <option
                                  v-for="vat in vats"
                                  :value="vat.id"
                                  :key="vat.id"
                                >
                                  {{ vat.name }}
                                </option>
                              </select>
                              <i></i>
                            </label>
                          </label>
                          <p
                            v-if="hasError('venues.0.manual_price_vat_id')"
                            class="text-danger"
                          >
                            {{ getError("venues.0.manual_price_vat_id") }}
                          </p>
                        </section>

                        <section class="flex-1 ml-1">
                          <label class="label">
                            {{ $label("booking.total_price", "Total price ") }}
                            (gross)
                          </label>
                          <label class="input">
                            <i class="icon-append fa fa-euro"></i>
                            <input
                              id="booking-manual-price-gross"
                              v-model="
                                selectedBooking.manual_price_amount_gross
                              "
                              type="number"
                              name="total_price"
                              :readonly="!selectedBooking.use_manual_price"
                              :class="{
                                'is-disabled': !selectedBooking.use_manual_price
                              }"
                            />
                          </label>
                          <p
                            v-if="
                              hasError('venues.0.manual_price_amount_gross')
                            "
                            class="text-danger"
                          >
                            {{ getError("venues.0.manual_price_amount_gross") }}
                          </p>
                        </section>
                      </div>

                      <div class="flex-between">
                        <section class="flex-1 mr-1">
                          <label class="label">
                            {{ $label("booking.net", "Net") }}
                          </label>
                          <label class="input">
                            <i class="icon-append fa fa-euro"></i>
                            <input
                              type="number"
                              id="booking-amount-net"
                              readonly
                              :class="{
                                'is-disabled': true
                              }"
                              :value="selectedBooking.amount_net"
                            />
                          </label>
                        </section>

                        <section class="flex-1 ml-1 mr-1">
                          <label class="label">
                            {{ $label("booking.vat", "Vat").toUpperCase() }}
                          </label>
                          <label class="input">
                            <i class="icon-append fa fa-euro"></i>
                            <input
                              type="number"
                              readonly
                              :class="{
                                'is-disabled': true
                              }"
                              id="booking-vat"
                              :value="selectedBooking.vat"
                            />
                          </label>
                        </section>

                        <section class="flex-1 ml-1">
                          <label class="label">
                            {{ $label("booking.gross", "Gross") }}
                            <a href="#"><i class="fa fa-info-circle"></i></a>
                          </label>
                          <label class="input">
                            <i class="icon-append fa fa-euro"></i>
                            <input
                              type="number"
                              id="booking-amount-gross"
                              readonly
                              :class="{
                                'is-disabled': true
                              }"
                              v-model="selectedBooking.amount_gross"
                            />
                          </label>
                        </section>
                      </div>

                      <div class="row">
                        <!-- BEGIN payment method -->
                        <section class="col col-6">
                          <label class="label">{{
                            $label("booking.payment_method", "Payment Method")
                          }}</label>
                          <label class="select">
                            <select
                              id="booking-payment-method"
                              v-model="selectedBooking.paymentMethod"
                              :disabled="updating"
                            >
                              <option
                                v-for="paymentType in paymentTypes"
                                :value="paymentType.id"
                                :key="paymentType.id"
                              >
                                {{ paymentType.slug }}
                              </option>
                            </select>
                          </label>
                          <p
                            v-if="hasError('payment_type_id')"
                            class="text-danger"
                          >
                            {{ getError("payment_type_id") }}
                          </p>
                        </section>
                        <!-- END payment method -->

                        <section class="col col-6 text-center">
                          <label class="label">{{ space }}</label>
                          <div style="padding-right: 15px;">
                            <button
                              type="button"
                              class="btn btn-primary btn-block v-form-button v-white is-disabled"
                              disabled
                            >
                              <i class="fa fa-plus"></i>
                              Extra
                            </button>
                          </div>
                        </section>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>

              <div class="col-sm-6">
                <h2>
                  <i class="fa fa-fw fa-lg fa-laptop"></i>
                  {{ $label("booking.calendar", "Calendar & Info Screen") }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <div class="row">
                        <section class="col col-6">
                          <label class="label">Zeile 1</label>
                          <label class="input">
                            <input
                              type="text"
                              value=""
                              name="booking_infoscreen_key"
                              placeholder="Titel"
                            />
                          </label>
                        </section>

                        <section class="col col-6">
                          <label class="label">Zeile 2</label>
                          <label class="input">
                            <input
                              type="text"
                              value=""
                              name="booking_infoscreen_value"
                              placeholder="Anzeigewert"
                            />
                          </label>
                        </section>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-8"><div class="col-xs-12">&nbsp;</div></div>
          </div>

          <hr />

          <div class="row">
            <div class="col-xs-12 text-center">
              <info-button
                class="mr-1"
                :class="{ 'is-disabled': updating }"
                id="booking-invest-button"
                icon="fa fa-fw fa-lg fa-check-square-o"
                :text="$label('booking.save', 'Save')"
                @click.native="createOrUpdateBooking"
              >
              </info-button>

              <warning-button
                class="mr-1"
                :class="{ 'is-disabled': updating }"
                id="cancel-booking-button"
                icon="fa fa-fw fa-lg fa-times"
                :text="$label('common.cancel', 'Cancel')"
                @click.native="cancelBooking"
              >
              </warning-button>

              <danger-button
                :class="{
                  'is-disabled': updating || selectedBookingId == null
                }"
                id="delete-booking-button"
                icon="fa fa-fw fa-lg fa-trash-o"
                :text="$label('common.delete', 'Delete')"
                @click.native="deleteBooking"
              >
              </danger-button>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-8"><div class="col-xs-12">&nbsp;</div></div>

      <div class="row mb-4">
        <div class="col-xs-12"></div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api";
import swal from "sweetalert";
import FormErrorsMixin from "@/mixins/FormErrors";

import Multiselect from "vue-multiselect";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

import "vue-swatches/dist/vue-swatches.css";

import "vue-multiselect-listbox/dist/vue-multi-select-listbox.css";

import ViewLoader from "@/components/Common/ViewLoader";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import WarningButton from "@/components/Common/Buttons/WarningButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import NewCustomerModal from "@/components/Customers/Customer/NewCustomerModal";
import SearchableSelect from "@/components/Common/Select/SearchableSelect";
import Error404 from "@/components/Common/Error404";

import "vue-slider-component/theme/default.css";

export default {
  name: "BookingVenue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    InfoButton,
    WarningButton,
    DangerButton,
    NewCustomerModal,
    DatePicker,
    Multiselect,
    SearchableSelect,
    Error404
  },

  data() {
    return {
      venueOptions: [],
      prices: [{ id: "Automatically", name: "Automatically" }],
      loading: false,
      noDataExist: false,
      space: "\xa0",
      updating: false,
      bookingName: null,
      showAddCustomerModal: false,
      dateFormat: "DD.MM.YYYY",
      timeFormat: "HH:mm",
      selectedBooking: {
        paymentMethod: null,
        remarks: null,
        customer: null,
        from: {
          date: null,
          time: null
        },
        to: {
          date: null,
          time: null
        },
        venues: [],
        price: "Automatically",
        discount_id: "",
        amount_net: null,
        amount_gross: null,
        vat: null,
        manual_price_vat_id: null,
        manual_price_amount_gross: null,
        use_manual_price: false
      },
      selectedBookingId: null,
      paymentTypes: [],
      discounts: [],
      customers: [],
      customerList: [],
      vats: [],
      validatedInputs: [
        "customer_id",
        "payment_type_id",
        "venues.0.venue_id",
        "venues.0.starts_at",
        "venues.0.ends_at",
        "venues.0.manual_price_amount_gross",
        "venues.0.manual_price_vat_id"
      ],
      debounce: null
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedBookingId = vm.$route.params.booking_id;
      if (
        vm.$route.query.venue_booking_deleted === true ||
        vm.$route.query.venue_booking_deleted === "true"
      ) {
        swal("Success!", "The venue booking was canceled.", "success");
      }
      if (
        typeof vm.selectedBookingId !== "undefined" &&
        vm.selectedBookingId !== null
      ) {
        vm.getVenueBooking();
        if (vm.$route.query.customer_created) {
          swal("Success!", "The Customer was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        if (vm.$route.query.booking_created) {
          swal("Success!", "The Booking was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
      }
      if (vm.$route.query.from_date_time) {
        vm.selectedBooking.from = {
          date: new Date(vm.$route.query.from_date_time),
          time: new Date(vm.$route.query.from_date_time)
        };
      }
      if (vm.$route.query.until_date_time) {
        vm.selectedBooking.to = {
          date: new Date(vm.$route.query.until_date_time),
          time: new Date(vm.$route.query.until_date_time)
        };
      }
      if (vm.$route.query.customer_id) {
        vm.getCustomer(vm.$route.query.customer_id, customer => {
          vm.selectedBooking.customer = {
            id: customer.id,
            name: `${customer.last_name} ${customer.first_name}`
          };
          vm.customers = [vm.selectedBooking.customer];
        });
      }
      vm.getFacilities();
      vm.getVats();
      vm.getPaymentTypes();
      vm.getDiscounts();
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.venue_booking_deleted === true) {
      swal("Success!", "The venue booking was canceled.", "success").then(
        () => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        }
      );
    }
    if (
      typeof this.selectedBookingId !== "undefined" &&
      this.selectedBookingId !== null
    ) {
      this.getVenueBooking();
    }
    this.getFacilities();
    this.getVats();
    this.getPaymentTypes();
    this.getDiscounts();
    next();
  },

  mounted: function() {
    this.getFacilities();
    this.getVats();
    this.getPaymentTypes();
    this.getDiscounts();
  },

  watch: {
    "selectedBooking.use_manual_price": function(newValue) {
      if (newValue) {
        this.selectedBooking.price = "Automatically";
      }
    },
    "selectedBooking.venues": function(newValue) {
      if (newValue.length !== 1) {
        this.prices = [{ id: "Automatically", name: "Automatically" }];
      } else {
        this.getVenuePrices(newValue[0].id, prices => {
          this.prices = [
            { id: "Automatically", name: "Automatically" },
            ...prices.map(price => ({
              id: price.id,
              name: `Selection: ${price.name} - ${price.amount_gross} EUR - ${price.vat.name} VAT.`
            }))
          ];
        });
      }
    }
  },

  methods: {
    cancelBooking() {
      this.$router.push({ name: "BookingVenues" });
    },

    deleteBooking() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The venue booking will be cancelled",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "bookings/" + this.selectedBookingId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "BookingVenues",
                  query: { venue_booking_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal("Success!", "The venue was deleted.", "success");
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete external venues.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    createOrUpdateBooking() {
      if (this.selectedBookingId == null) {
        this.createBooking();
      } else {
        this.updateBooking();
      }
    },

    generateSavingData() {
      return {
        customer_id: this.selectedBooking.customer?.id,
        payment_type_id: this.selectedBooking.paymentMethod,
        internal_note: this.selectedBooking.remarks,
        venues: this.selectedBooking.venues.map(venue => ({
          venue_id: venue.id,
          discount_id: this.selectedBooking.discount_id,
          places_used: 1,
          starts_at:
            this.convertDateTime(
              `${this.$moment(this.selectedBooking.from.date).format(
                this.dateFormat
              )} ${this.$moment(this.selectedBooking.from.time).format(
                this.timeFormat
              )}`
            ) + ":00",
          ends_at:
            this.convertDateTime(
              `${this.$moment(this.selectedBooking.to.date).format(
                this.dateFormat
              )} ${this.$moment(this.selectedBooking.to.time).format(
                this.timeFormat
              )}`
            ) + ":00",
          use_manual_price: this.selectedBooking.use_manual_price,
          use_automatic_price:
            !this.selectedBooking.use_manual_price &&
            this.selectedBooking.price === "Automatically",
          use_selected_price:
            !this.selectedBooking.use_manual_price &&
            this.selectedBooking.price !== "Automatically",
          selected_price_id:
            this.selectedBooking.price !== "Automatically"
              ? this.selectedBooking.price
              : null,
          manual_price_amount_gross: this.selectedBooking
            .manual_price_amount_gross,
          manual_price_vat_id: this.selectedBooking.manual_price_vat_id
        }))
      };
    },

    getVenuePrices(id, successCallback) {
      const endpoint = this.$partnerEndpoint("venues/" + id + "/prices");

      Api()
        .get(endpoint)
        .then(response => {
          if (typeof successCallback == "function") {
            successCallback(response.data.data);
          }
        });
    },

    getVenueBooking() {
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        `bookings/${this.selectedBookingId}`
      );

      Api()
        .get(endpoint)
        .then(response => {
          const venueBooking = response.data.data;
          const customer = venueBooking.customer;

          this.selectedBooking.customer = {
            id: customer.id,
            name: `${customer.last_name} ${customer.first_name}`
          };
          this.bookingName = "Venue Booking";
          this.selectedBooking.paymentMethod = venueBooking.payment_type.id;
          this.selectedBooking.remarks = venueBooking.internal_note;
          this.selectedBooking.venues = venueBooking.venue_bookings.map(
            item => item.venue
          );
          this.selectedBooking.price = "Automatically";
          if (venueBooking.venue_bookings.length > 0) {
            this.selectedBooking.from = {
              date: new Date(venueBooking.venue_bookings[0].starts_at),
              time: new Date(venueBooking.venue_bookings[0].starts_at)
            };
            this.selectedBooking.to = {
              date: new Date(venueBooking.venue_bookings[0].ends_at),
              time: new Date(venueBooking.venue_bookings[0].ends_at)
            };
            this.selectedBooking.discount_id = venueBooking.venue_bookings[0]
              .discount
              ? venueBooking.venue_bookings[0].discount.id
              : "";

            const venuePrice = venueBooking.venue_bookings[0].venue_price;
            if (venuePrice.type === "manual") {
              this.selectedBooking.use_manual_price = true;
              this.selectedBooking.manual_price_vat_id = venuePrice.vat
                ? venuePrice.vat.id
                : null;
              this.selectedBooking.manual_price_amount_gross =
                venuePrice.amount_gross;
            } else {
              this.selectedBooking.price = venueBooking.venue_bookings[0]
                .automatic_price
                ? "Automatically"
                : venuePrice.id;
            }
          }
          this.selectedBooking.amount_net = venueBooking.total_price.amount_net;
          this.selectedBooking.amount_gross =
            venueBooking.total_price.amount_gross;
          this.selectedBooking.vat = venueBooking.total_price.vat_amount;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    createBooking() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("bookings/venues");
      const bookingInfo = this.generateSavingData();

      Api()
        .post(endpoint, bookingInfo)
        .then(response => {
          this.selectedBookingId = response.data.data.id;
          this.$router.push({
            name: "BookingVenueDetails",
            params: { booking_id: this.selectedBookingId },
            query: { booking_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    updateBooking() {
      if (this.loading || this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "bookings/venues/" + this.selectedBookingId
      );
      const bookingInfo = this.generateSavingData();

      Api()
        .put(endpoint, bookingInfo)
        .then(() => {
          this.getVenueBooking();
          swal({
            title: "Success!",
            text: "The booking was updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    onSearchChange(query) {
      if (query.length >= 2) {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => {
          this.getCustomers(query);
        }, 600);
      } else {
        this.customers = [];
      }
    },

    getCustomer(customerId, successCallback) {
      const endpoint = this.$partnerEndpoint("customers/" + customerId);

      return Api()
        .get(endpoint)
        .then(response => {
          if (typeof successCallback == "function") {
            successCallback(response.data.data);
          }
        })
        .catch(error => {
          console.log(error.response);
        });
    },

    getCustomers(query = "") {
      const endpoint = this.$partnerEndpoint("customers");

      return Api()
        .get(endpoint, {
          params: {
            search: query
          }
        })
        .then(response => {
          this.customers = response.data.data.map(
            ({ id, first_name, last_name }) => ({
              id,
              name: `${last_name} ${first_name}`
            })
          );
        })
        .catch(error => {
          console.log(error.response);
        });
    },

    getFacilities(successCallback) {
      const endpoint = this.$partnerEndpoint("facilities/simplified");

      Api()
        .get(endpoint)
        .then(response => {
          this.venueOptions = [];

          this.facilities = response.data.data;
          this.facilities.map(facility => {
            facility.venues.map(venue => {
              this.venueOptions.push(venue);
            });
          });
          if (this.$route.query.venue_ids) {
            const venue_ids =
              typeof this.$route.query.venue_ids === "object"
                ? this.$route.query.venue_ids
                : [this.$route.query.venue_ids];
            this.selectedBooking.venues = this.venueOptions.filter(
              venueOption =>
                venue_ids.find(venueId => venueOption.id === parseInt(venueId))
            );
          }
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    getPaymentTypes() {
      this.loading = true;
      const endpoint = this.$partnerEndpoint("payment-types");

      Api()
        .get(endpoint)
        .then(response => {
          this.paymentTypes = response.data.data;
          if (!this.selectedBookingId && this.paymentTypes.length > 0) {
            this.selectedBooking.paymentMethod = this.paymentTypes[0].id;
          }
        })
        .catch(error => {
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getDiscounts() {
      this.loading = true;
      const endpoint = this.$partnerEndpoint("discounts");

      Api()
        .get(endpoint)
        .then(response => {
          this.discounts = response.data.data;
        })
        .catch(error => {
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getVats() {
      this.loading = true;
      const endpoint = this.$partnerEndpoint("vats");

      Api()
        .get(endpoint)
        .then(response => {
          this.vats = response.data.data;
          if (this.selectedBookingId == null && this.vats.length > 0) {
            this.selectedBooking.manual_price_vat_id = this.vats[0].id;
          }
        })
        .catch(error => {
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    openAddCustomerModal() {
      this.showAddCustomerModal = true;
    },

    closeAddCustomerModal() {
      this.showAddCustomerModal = false;
    },

    onSelectCustomer(customer) {
      this.showAddCustomerModal = false;
      this.selectedBooking.customer = {
        id: customer.id,
        name: `${customer.last_name} ${customer.first_name}`
      };
      this.customers = [this.selectedBooking.customer];
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.mr-1 {
  margin-right: 1rem;
}

.ml-1 {
  margin-left: 1rem;
}

.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-8 {
  margin-top: 2rem;
}

.flex-1 {
  flex: 1;
}

.flex-between {
  display: flex;
  justify-content: space-between;
}

.user-plus-button {
  padding: 0 1.5rem;
  font-size: 2rem;
}
</style>
